class DeferredMedia extends HTMLElement {
  isPlaying = false;
  mediaPoster = null;
  toggleMediaButton = null;

  connectedCallback() {
    this.mediaPoster = this.querySelector('[data-ref="deferred-media-poster"]');
    this.toggleMediaButton = this.querySelector('[data-ref="toggle-media-button"]');

    this.mediaPoster?.addEventListener('click', this.showDeferredMedia.bind(this));
    this.toggleMediaButton?.addEventListener('click', this.toggleMedia.bind(this));
  }

  disconnectedCallback() {}

  updatePlayPauseHint(isPlaying) {
    if (this.toggleMediaButton instanceof HTMLElement) {
      this.toggleMediaButton.classList.remove('hidden');

      const playIcon = this.toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);

      const pauseIcon = this.toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  showDeferredMedia = () => {
    // Le clic est un geste utilisateur : le son est donc autorise.
    // ORDRE CRITIQUE — la preparation se fait AVANT loadContent, sur le <template>,
    // donc avant que la video entre dans le DOM. Demuter APRES le demarrage fait
    // figer l'image sur iOS : la video rendue par snippets/video.liquid porte
    // `autoplay` + `muted`, ce qui la classe cote iOS comme "demarrage automatique",
    // un mode ou la lecture non-muette en ligne n'est pas permise. iOS garde alors
    // l'audio et coupe le rendu des images. (Constate le 26/08/2026.)
    this.prepareUnmutedPlayback();
    this.loadContent(true);
    this.playUnmuted();
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  // Retire `muted` et `autoplay` de la source AVANT le clone, pour qu'iOS voie
  // une lecture ordinaire declenchee au doigt. `playsinline` est indispensable :
  // sans lui iOS ouvre son lecteur plein ecran.
  prepareUnmutedPlayback() {
    const source = this.querySelector('template')?.content.firstElementChild;
    if (!(source instanceof HTMLElement) || source.tagName !== 'VIDEO') return;

    source.removeAttribute('muted');
    source.removeAttribute('autoplay');
    source.setAttribute('playsinline', '');
    source.setAttribute('webkit-playsinline', '');
  }

  // `autoplay` ayant ete retire, loadContent ne lance plus la lecture : on s'en charge.
  playUnmuted() {
    const video = this.querySelector('video');
    if (!video) return;

    video.muted = false;
    video.volume = 1;

    const played = video.play();
    if (played && typeof played.catch === 'function') {
      // Filet de securite : si un navigateur refuse la lecture non-muette,
      // on retombe sur une lecture muette plutot que sur une video figee.
      played.catch(() => {
        video.muted = true;
        video.play();
      });
    }
  }

  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');

    this.querySelector('[data-ref="deferred-media-video"]')?.appendChild(content);

    // this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.querySelector('[data-ref="deferred-media-poster"]')?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // force autoplay for safari
      content.play();
    }
  }

  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*',
      );
    } else {
      this.querySelector('video')?.play();
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*',
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}
